import React, { useState, useMemo } from 'react'
import DismissButton from 'components/dismiss-button'
import { BlueButton } from 'components/blue-button'
import { showToast } from 'components/toast'
import { Lesson, Class } from '@all-for-one/shared-types'
import { DraftKeyPdfProps } from './pdf-draft-answer-key'
import { getLogoPngDataUrl, getPdfDownloadDate, downloadPdf } from 'util/pdf-download'
import printStyles from 'css/components/lesson-builder/print-overlay.module.css'

/**
 * The shared "download a filtered, sorted PDF of this lesson's questions"
 * flow -- was duplicated byte-for-byte (minus which PDF renderer and a
 * handful of labels) between print-draft-overlay.tsx and
 * print-answer-key-overlay.tsx, and had quietly drifted apart in the
 * process: the two screens' student-count display and sort-order wording
 * no longer matched, purely from being maintained as separate copies. One
 * brick now; print-draft-overlay.tsx and print-answer-key-overlay.tsx are
 * thin, purpose-named wrappers over it, per each screen's real config.
 */
export interface PrintOverlayProps {
    lesson: Lesson
    className?: string
    gradeBand?: string
    subject?: string
    classData?: Class
    onDismiss: () => void
    /** Which PDF renderer this download produces. */
    PdfDocument: React.FC<DraftKeyPdfProps>
    /** Suffix before ".pdf" in the downloaded filename, e.g. "draft" or "answer_key". */
    filenameSuffix: string
    /** Overlay header text, e.g. "Download draft questions". */
    headerText: string
    /** Step-3 heading, e.g. "How would you like to organize it?". Screen-specific wording is intentional here. */
    organizeStepHeading: string
}

const SECTION_MAP: Record<string, string> = {
    assessment: 'Pre/Post Assessment',
    cfu: 'Check for Understanding',
    iw: 'Independent Work',
}

const SORT_LABELS: Record<string, string> = {
    'section-level': 'Section to Level',
    'level-section': 'Level to Section',
}

export const PrintOverlay = ({
    lesson,
    className: assignedClassName,
    gradeBand,
    subject,
    classData,
    onDismiss,
    PdfDocument,
    filenameSuffix,
    headerText,
    organizeStepHeading,
}: PrintOverlayProps) => {
    const [step, setStep] = useState(1)
    const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set())
    const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set())
    const [sortOrder, setSortOrder] = useState<'section-level' | 'level-section'>('section-level')
    const [downloading, setDownloading] = useState(false)

    const toggleSection = (key: string) => {
        setSelectedSections(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleLevel = (level: string) => {
        setSelectedLevels(prev => {
            const next = new Set(prev)
            if (next.has(level)) next.delete(level)
            else next.add(level)
            return next
        })
    }

    const studentCounts = useMemo(() => {
        if (!classData?.students) return null
        return {
            Emerging: classData.students.filter(s => s.difficulty === 'Emerging').length,
            Intermediate: classData.students.filter(s => s.difficulty === 'Intermediate').length,
            Advanced: classData.students.filter(s => s.difficulty === 'Advanced').length,
        }
    }, [classData])

    const canAdvanceFromStep1 = selectedSections.size > 0
    const canAdvanceFromStep2 = selectedLevels.size > 0

    const sectionKeys = ['assessment', 'cfu', 'iw'] as const
    const levelKeys = ['Emerging', 'Intermediate', 'Advanced'] as const
    const allSectionsSelected = selectedSections.size === sectionKeys.length
    const allLevelsSelected = selectedLevels.size === levelKeys.length
    const toggleSelectAllSections = () => setSelectedSections(allSectionsSelected ? new Set() : new Set(sectionKeys))
    const toggleSelectAllLevels = () => setSelectedLevels(allLevelsSelected ? new Set() : new Set(levelKeys))

    const handleDownload = async () => {
        setDownloading(true)
        try {
            const logoDataUrl = await getLogoPngDataUrl()
            const doc = (
                <PdfDocument
                    lesson={lesson}
                    assignedClassName={assignedClassName}
                    gradeBand={gradeBand}
                    subject={subject}
                    logoDataUrl={logoDataUrl}
                    downloadDate={getPdfDownloadDate()}
                    selectedSections={selectedSections}
                    selectedLevels={selectedLevels}
                    sortOrder={sortOrder}
                />
            )
            await downloadPdf(doc, `${lesson.name}_${filenameSuffix}.pdf`)
            onDismiss()
        } catch (error) {
            console.error('Error generating PDF:', error)
            showToast("We couldn't generate that PDF. Please try again.")
        } finally {
            setDownloading(false)
        }
    }

    const summaryText = useMemo(() => {
        const sectionNames = ['assessment', 'cfu', 'iw']
            .filter(k => selectedSections.has(k))
            .map(k => SECTION_MAP[k])
            .join(' + ')
        const levelNames = ['Emerging', 'Intermediate', 'Advanced']
            .filter(l => selectedLevels.has(l))
            .join(' + ')
        return `You are downloading: ${sectionNames} · ${levelNames} · Sort: ${SORT_LABELS[sortOrder]}`
    }, [selectedSections, selectedLevels, sortOrder])

    return (
        <div className="overlay">
            <div className={`overlay-content-container ${printStyles.printModal}`}>
                <div className="overlay-content-header">
                    {headerText}
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className={printStyles.progress}>
                    {[1, 2, 3, 4].map(dot => (
                        <div key={dot} className={`${printStyles.progressDot} ${dot <= step ? printStyles.progressDotDone : ''}`} />
                    ))}
                </div>
                <div className="overlay-content-body">
                    {step === 1 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which sections do you want to include?</h3>
                            <label className={`${printStyles.checkboxLabel} ${printStyles.selectAllLabel}`}>
                                <input
                                    type="checkbox"
                                    checked={allSectionsSelected}
                                    onChange={toggleSelectAllSections}
                                />
                                Select all
                            </label>
                            <div className={printStyles.divider} />
                            {sectionKeys.map(key => (
                                <label key={key} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.has(key)}
                                        onChange={() => toggleSection(key)}
                                    />
                                    {SECTION_MAP[key]}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <div />
                                <BlueButton label="Next" onClick={() => setStep(2)} disabled={!canAdvanceFromStep1} />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which levels do you want to include?</h3>
                            <label className={`${printStyles.checkboxLabel} ${printStyles.selectAllLabel}`}>
                                <input
                                    type="checkbox"
                                    checked={allLevelsSelected}
                                    onChange={toggleSelectAllLevels}
                                />
                                Select all
                            </label>
                            <div className={printStyles.divider} />
                            {levelKeys.map(level => (
                                <label key={level} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedLevels.has(level)}
                                        onChange={() => toggleLevel(level)}
                                    />
                                    {level}{studentCounts ? <span className={printStyles.count}>{` (${studentCounts[level]} student${studentCounts[level] === 1 ? '' : 's'})`}</span> : null}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(1)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(3)} disabled={!canAdvanceFromStep2} />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>{organizeStepHeading}</h3>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sortOrder"
                                    checked={sortOrder === 'section-level'}
                                    onChange={() => setSortOrder('section-level')}
                                />
                                Section to Level
                            </label>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sortOrder"
                                    checked={sortOrder === 'level-section'}
                                    onChange={() => setSortOrder('level-section')}
                                />
                                Level to Section
                            </label>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(2)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(4)} />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Summary</h3>
                            <p className={printStyles.summaryText}>{summaryText}</p>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(3)}>Back</button>
                                <BlueButton label={downloading ? 'Generating…' : 'Download'} onClick={handleDownload} disabled={downloading} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
